import * as React from 'react'
import { useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client/react'

import {
  clearPendingEngagementIds,
  requestNewEngagements,
  useHasPendingEngagements,
} from '../hooks/engagement/newEngagementSignal'
import type { BaseCommunityFilters } from '../CommunityFilters/types'
import { useFilters } from '../CommunityFilters/useFilters'
import { useNewCommentsAvailableButton } from '../hooks/backfill/useNewCommentsAvailableButton'
import { useIsPostsPanelCollapsed } from '../hooks/ui/postsPanelSignal'
import { useCommentsQuery } from '../hooks/comments/useCommentsQuery'
import { NewItemsAvailableButton } from '../NewItemsAvailableButton'
import { useIsByPostView } from '../util/routeUtils'

import type { JSX } from 'react'

type NewCommentsAvailableButtonProps =
  | { channelId: string; channelIds?: never }
  | { channelId?: never; channelIds: string[] }

export const NewCommentsAvailableButton = (
  channelProps: NewCommentsAvailableButtonProps,
): JSX.Element => {
  const [isFetchingNewComments, setIsFetchingNewComments] =
    React.useState(false)
  const channelId =
    'channelId' in channelProps ? channelProps.channelId : undefined
  const channelIds =
    'channelIds' in channelProps ? channelProps.channelIds : undefined
  const client = useApolloClient()

  const {
    filters: { status, sortOption, publishedAt },
  } = useFilters()
  // Present only in the by-post view routes; scopes the refetch to that post.
  const { postId } = useParams<{ postId?: string }>()
  const filters = React.useMemo<BaseCommunityFilters>(
    () => ({ status, sortOption, publishedAt, postId }),
    [status, sortOption, publishedAt, postId],
  )

  const isByPostView = useIsByPostView()
  const isPostsPanelCollapsed = useIsPostsPanelCollapsed()

  const { showNewCommentsButton, hideNewCommentsButton } =
    useNewCommentsAvailableButton({ channelId, channelIds, postId })
  const { fetchNewComments } = useCommentsQuery({
    channelIds: channelIds ?? (channelId ? [channelId] : undefined),
    filters,
    skip: true,
  })

  const handleFetchNewComments = async (): Promise<void> => {
    setIsFetchingNewComments(true)
    // Tell every mounted list a jump was asked for, so it scrolls toward where
    // the new comments will appear and can spot them once they land.
    requestNewEngagements()
    try {
      // In the all-channels by-post view, multiple Comments queries may be active
      // (one per post card). A broad refetchQueries ensures all of them update,
      // whereas fetchNewComments only refetches the single query this hook owns.
      const isAllChannelsByPost = channelIds && channelIds.length > 0 && postId
      if (isAllChannelsByPost) {
        await client.refetchQueries({ include: ['Comments'] })
      } else {
        await fetchNewComments()
      }
      // Every sort but oldest-first lands new comments on the first page, which
      // the refetch above reloaded. An id still pending after that can never
      // show up: a filter hides it, or no list rendered it. It has to be
      // dropped, or it keeps the pill on screen with nothing left to reveal.
      if (sortOption !== 'oldest') {
        clearPendingEngagementIds('comment')
      }
    } catch {
      // The fetch reports its own failure. Leaving the ingested ids pending
      // keeps the pill on screen, which is the only way back to comments that
      // never arrived.
    } finally {
      setIsFetchingNewComments(false)
      hideNewCommentsButton()
    }
  }

  // Under oldest-first the new comments sort onto the last page, so they can be
  // several pages below. Keep the pill up until the user has actually reached
  // them, giving them something to click to keep moving down the list.
  const hasUnreachedComments = useHasPendingEngagements('comment')

  const isValidFilter = status === 'all' || status === 'unreplied'
  const shouldShow =
    isValidFilter &&
    (isFetchingNewComments || showNewCommentsButton || hasUnreachedComments)

  return (
    <NewItemsAvailableButton
      show={shouldShow}
      isFetching={isFetchingNewComments}
      label="New comments available"
      onClick={handleFetchNewComments}
      placement={isByPostView ? 'commentsColumn' : 'page'}
      panelCollapsed={isPostsPanelCollapsed}
      arrowDown={sortOption === 'oldest'}
    />
  )
}
